import React, { useState, useRef, useEffect } from 'react';
import { Pencil, Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';

interface InlineEditableNameProps {
  name: string;
  onSave: (newName: string) => Promise<void>;
  disabled?: boolean;
  className?: string;
  maxLength?: number;
  placeholder?: string;
}

const InlineEditableName: React.FC<InlineEditableNameProps> = ({
  name,
  onSave,
  disabled = false,
  className,
  maxLength = 100,
  placeholder = 'Enter name…',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(name);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep local value in sync if the external name changes while not editing
  useEffect(() => {
    if (!isEditing) {
      setValue(name);
    }
  }, [name, isEditing]);

  // Auto-focus the input when entering edit mode
  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  const enterEditMode = () => {
    setValue(name);
    setError(null);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setValue(name);
    setError(null);
    setIsEditing(false);
  };

  const commitSave = async () => {
    const trimmed = value.trim();

    // No-op: nothing changed
    if (trimmed === name) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(trimmed);
      setIsEditing(false);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'An error occurred whilst saving.';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitSave();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const handleBlur = () => {
    if (!isSaving) {
      commitSave();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    // Clear error on next keystroke
    if (error) {
      setError(null);
    }
  };

  if (isEditing) {
    return (
      <span className={cn('inline-flex flex-col', className)}>
        <span className="inline-flex items-center gap-1.5">
          <input
            ref={inputRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            disabled={isSaving}
            maxLength={maxLength}
            placeholder={placeholder}
            className="px-2 py-0.5 border border-primary-500 rounded text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          />
          {isSaving && (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400 shrink-0" aria-label="Saving…" />
          )}
        </span>
        {error && (
          <span className="text-xs text-red-500 mt-0.5" role="alert">
            {error}
          </span>
        )}
      </span>
    );
  }

  return (
    <span className={cn('inline-flex items-center gap-1.5 group', className)}>
      <span className="font-semibold text-gray-900 truncate">{name}</span>
      {!disabled && (
        <button
          type="button"
          onClick={enterEditMode}
          aria-label="Edit name"
          className="p-0.5 rounded text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
        >
          <Pencil className="h-4 w-4" />
        </button>
      )}
    </span>
  );
};

export default InlineEditableName;
